import { GetCoinListTypeEnum } from '@/enums/businessEnum';
import CacheEnum from '@/enums/cacheEnum';
import { useLocalStorage } from '@/hooks/useStorage';
import {
  getMemberCollectApi,
  getTradingSymbolsApi,
} from '@/services/api/requestApi';
import { BulkTypeEnum } from '@/enums/businessEnum';
import { useReq } from '@/services/net/request';
import { useModel } from '@umijs/max';
import { useMount } from 'ahooks';

/**
 * 使用本地存储和远程请求获取股票列表和已收藏硬币列表的钩子。
 *
 * 该钩子不接受任何参数，并返回以下对象：
 * @returns {Object} 返回包含以下属性的对象：
 *  - setLocalStock: 用于设置本地存储中股票信息的函数。
 *  - localStock: 从本地存储中获取的股票信息。
 *  - getStockListReq: 异步获取股票列表的函数。
 *  - stockList: 股票列表的数据。
 *  - collectedCoinList: 已收藏硬币的列表。
 */
export default () => {
  // 使用本地存储钩子来管理股票信息
  const [localStock, setLocalStock] = useLocalStorage<any>({
    key: CacheEnum.APP_LOCAL_STOCK,
    defaultValue: null,
  });
  // 使用socket模型来发送消息
  const { sendMsg } = useModel('socket');
  const { user } = useModel('user');
  // 异步获取股票列表
  const { data: stockList = [] as any, runAsync: getStockListReq } = useReq(
    (key: string) => getTradingSymbolsApi({
      type: GetCoinListTypeEnum.STOCK,
      stockSubType: BulkTypeEnum.COPY_TRADING,
      bulk: 1
    }),
    {
      manual: false,
      cacheKey: CacheEnum.PAGE_HOME_CONTRACT_LIST,
      formatResult: (res: any) => {
        const { list } = res?.data || [];
        return list ?? [];
      },
      onSuccess: (res: any) => {
        // 成功获取数据后，更新本地存储和发送订阅消息
        if (res?.[0]) {
          if (!localStock) {
            setLocalStock(res[0]);
          }

          sendMsg(
            `{"type":"SUB","data": ${JSON.stringify(
              res.map((i: { symbol: any }) => i.symbol),
            )}}`,
          );
        }
      },
    },
  );

  // 获取已收藏股票列表
  const { data: collectedCoinList, runAsync: collectedRequest } = useReq(
    () => getMemberCollectApi({ isTripartite: 1, pageSize: 999 }),
    {
      formatResult: (res: any) => {
        const { list = [] } = res?.data || [];
        let arr = [];
        arr = list.map((item: any) => {
          return {
            ...item,
            id: item?.sourceId,
          };
        });
        return arr;
      },
      manual: true,
    },
  );
  useMount(async () => {
    if (user?.id) {
      await collectedRequest();
    }
  });

  return {
    setLocalStock,
    localStock,
    getStockListReq,
    stockList,
    collectedCoinList,
    collectedRequest,
  };
};
